<template>
  <div :class="bem.b()">
    <div :class="bem.m('preview-box')">
      <a-spin size="small" :spinning="imgLoading">
        <a-upload
          v-model:file-list="fileList"
          v-bind="getBindValue"
          :accept="computedAccept"
          :show-upload-list="false"
          :max-count="maxCount"
          :multiple="computedMultiple"
          :before-upload="beforeUpload"
          :custom-request="customRequest"
          :class="[bem.m('upload'), bem.is(getBorderType)]"
          :style="{
            width: getSizeStyle(width + 2),
            height: getSizeStyle(height + 2),
            borderRadius: borderRadius,
          }"
          list-type="picture-card"
          @dragenter.capture="handleDragEvent"
          @dragover.capture="handleDragEvent"
          @drop.capture="handleDragEvent"
        >
          <ll-image
            v-if="innerValue.length > 0"
            v-bind="getImgBindValue"
            :preview="{ visible: false }"
            :width="getSizeStyle(width)"
            :height="getSizeStyle(height)"
            border-radius="0"
            :src="innerValue[0]"
            :is-show-border="false"
          >
            <template #previewMaskContent>
              <div :class="bem.m('preview-mask')">
                <ll-icon
                  v-if="isPreview"
                  :icon-name="`icon-pwd-look`"
                  :class="bem.e('image-icon')"
                  @click.stop="imageVisible = true"
                />

                <ll-icon
                  v-if="isDelete"
                  :icon-name="`icon-delete`"
                  :class="bem.e('image-icon')"
                  @click.stop="handleRemoveImage"
                />
                <div></div>
              </div>
            </template>
          </ll-image>

          <!-- 无图片时展示图标 -->
          <div v-else-if="!$slots.iconRender" :class="bem.e('upload-content')">
            <ll-icon
              :icon-name="uploadIcon || 'icon-add'"
              :size="uploadIconSize"
            />
            <!-- 上传标题 -->
            <div v-if="uploadTitle" :class="bem.m('upload-title')">
              {{ uploadTitle }}
            </div>
            <!-- 上传提示 -->
            <div v-if="computedTip" :class="bem.m('upload-tip')">
              {{ computedTip }}
            </div>
          </div>
          <slot v-else name="iconRender"> </slot>
        </a-upload>
      </a-spin>
    </div>

    <!-- 预览展示 -->
    <div style="display: none">
      <a-image-preview-group
        :preview="{
          visible: imageVisible,
          onVisibleChange: (vis: boolean) => (imageVisible = vis),
        }"
      >
        <a-image v-for="item in innerValue" :key="item" :src="item" />
      </a-image-preview-group>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  createNamespace,
  fileToBase64,
  formatFileSize,
  matchAcceptType,
} from '@ll-plus/utils'
import { Form, message } from 'ant-design-vue'
import { computed, ref, useAttrs, watch } from 'vue'
import { uploadImageEmits, uploadImageProps } from './upload-image'

import type {
  FileType,
  UploadProps,
} from 'ant-design-vue/es/upload/interface.d.ts'
import { omit } from 'lodash-es'
import { imageProps } from '../../image'
import { watermarkProps } from '../../watermark'

//1.Init props/emits/bem/attrs

defineOptions({ name: 'LlUploadImage' })

const props = defineProps(uploadImageProps)

const emits = defineEmits(uploadImageEmits)

const bem = createNamespace('upload-image')

const attrs = useAttrs()

const fileList = ref([])

const imageVisible = ref(false)

const innerValue = ref<string[]>(
  Array.isArray(props.value)
    ? [...props.value]
    : props.value
      ? [props.value]
      : [],
)

const formItemContext = Form.useInjectFormItemContext()

// 获取绑定给上传组件的属性
const getBindValue = computed(() => ({
  ...omit({ ...attrs }, [...Object.keys(imageProps)]),
}))

// 获取绑定给图片组件的属性
const getImgBindValue = computed(() => {
  const imagePropKeys = Object.keys({ ...imageProps, ...watermarkProps })
  return Object.fromEntries(
    Object.entries(attrs).filter(([key]) => imagePropKeys.includes(key)),
  )
})

// 尺寸样式处理函数
function getSizeStyle(size: any) {
  // 处理数字类型
  if (typeof size === 'number') {
    return `${size}px`
  }
  // 处理字符串类型
  if (typeof size === 'string') {
    return size
  }
  // 处理其他情况
  return undefined
}

// 在捕获阶段阻止拖拽事件
const handleDragEvent = (e: DragEvent) => {
  if (!props.isDrag) {
    e.preventDefault()
    e.stopPropagation()
  }
}

const getBorderType = computed(() => {
  switch (props.borderType) {
    // 虚线
    case 'dashed':
      return 'dashed'
    // 实线
    case 'solid':
      return 'solid'
    // 点线
    case 'dotted':
      return 'dotted'
    default:
      return ''
  }
})

const computedAccept = computed(() => {
  return props.accept
})

const computedTip = computed(() => {
  return typeof props.uploadTip === 'string'
    ? props.uploadTip
    : props.uploadTip
      ? `支持${computedAccept.value}格式，不超过${formatFileSize(
          props.imgSize * 1024 * 1024,
        )}。`
      : ''
})

const computedMultiple = computed(() => props.maxCount > 1)

const beforeUpload = async (file: FileType, FileList: FileType[]) => {
  const isType = matchAcceptType(file, computedAccept.value)

  // 将文件大小从字节转换为MB
  const fileSizeInMB = file.size / (1024 * 1024)

  // 如果props.imgSize为0，表示无大小限制
  const isSize = props.imgSize === 0 ? true : fileSizeInMB <= props.imgSize

  if (!isType) {
    message.error(`${file.name} 需要为${computedAccept.value}格式。`)
    return false
  }

  if (!isSize) {
    // 使用格式化函数显示限制大小
    message.error(
      `${file.name} 文件不得大于${formatFileSize(
        props.imgSize * 1024 * 1024,
      )}。`,
    )
    return false
  }

  return isType && isSize && props.beforeUpload?.(file, FileList)
}

const customRequest: UploadProps['customRequest'] = async (res: any) => {
  const fileUrl = (await fileToBase64(res.file)) as string
  if (props.useBase64) {
    props.maxCount === 1
      ? (innerValue.value = [fileUrl])
      : innerValue.value.push(fileUrl)
  }
  try {
    props.customRequest?.(res, fileUrl)
  } catch (error) {
    innerValue.value.splice(innerValue.value.length - 1, 1)
    message.error('上传失败')
  }
}

// 移除图片
const handleRemoveImage = async () => {
  const index = innerValue.value.length - 1
  innerValue.value.splice(index, 1)

  setValue()
}

const setValue = () => {
  const value =
    props.maxCount === 1
      ? innerValue.value.length
        ? innerValue.value[0]
        : ''
      : innerValue.value
  emits('update:value', value)
  emits('change', value)
  formItemContext.onFieldChange()
}

watch(
  () => props.value,
  (newVal) => {
    if (newVal !== innerValue.value) {
      innerValue.value = Array.isArray(newVal)
        ? [...newVal]
        : newVal
          ? [newVal]
          : []
    }
  },
)

watch(
  innerValue,
  () => {
    setValue()
  },
  {
    deep: true,
  },
)
</script>
